import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, CardHeader, Button, Row, Col } from 'reactstrap';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const TOKEN_VALIDITY_DURATION = 30000; // 30 secondi

function getEuropeRomeOffsetMinutes(date) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Rome',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'shortOffset',
  });

  const parts = formatter.formatToParts(date);
  const timeZoneName = parts.find((part) => part.type === 'timeZoneName')?.value || 'GMT+1';
  const match = timeZoneName.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);

  if (!match) {
    return 60;
  }

  const sign = match[1] === '-' ? -1 : 1;
  const hours = parseInt(match[2], 10);
  const minutes = match[3] ? parseInt(match[3], 10) : 0;
  return sign * (hours * 60 + minutes);
}

function createDateInEuropeRome(year, month, day, hour, minute = 0, second = 0) {
  const utcTimestamp = Date.UTC(year, month, day, hour, minute, second);
  const offsetMinutes = getEuropeRomeOffsetMinutes(new Date(utcTimestamp));
  return new Date(utcTimestamp - offsetMinutes * 60 * 1000);
}

export function getLastDayBefore(date) {
  const d = new Date(date);
  // Calcola il lunedì della settimana che contiene la data (settimana che inizia di lunedì)
  const day = d.getDay(); // 0 = domenica, 1 = lunedì, ..., 6 = sabato
  const diffToMonday = (day + 6) % 7; // 0 per lunedì, 1 per martedì, ..., 6 per domenica
  const monday = new Date(d);
  monday.setDate(d.getDate() - diffToMonday);
  // Il mercoledì della settimana precedente è 5 giorni prima del lunedì corrente
  const prevWednesday = new Date(monday);
  prevWednesday.setDate(monday.getDate() - 5);

  return createDateInEuropeRome(
    prevWednesday.getFullYear(),
    prevWednesday.getMonth(),
    prevWednesday.getDate(),
    18,
    0,
    0
  );
}

export function canCancelAppointment(appointmentDate, now = new Date()) {
  const lastDayToCancel = getLastDayBefore(appointmentDate);
  return new Date(now) <= lastDayToCancel;
}

const CancellaPrenotazione = ({ currentSlot, selectedSlot, setSelectedSlot, setSlots, showAlert, setCurrentSlot, associatedID, voucherID, fetchSlots, handleValidazione, associatedName  }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [token, setToken] = useState(null);
  const [tokenTimestamp, setTokenTimestamp] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false); // nuovo stato
  const [isDownloading, setIsDownloading] = useState(false); // nuovo stato


  // Genera il token solo se necessario
  useEffect(() => {
    const generateToken = async () => {
      if (!executeRecaptcha) return;
      const now = Date.now();
      if (!token || !tokenTimestamp || now - tokenTimestamp >= TOKEN_VALIDITY_DURATION) {
        const newToken = await executeRecaptcha('prenotazione');
        setToken(newToken);
        setTokenTimestamp(now);
        console.log('Token reCAPTCHA aggiornato');
      } else {
        console.log('Token reCAPTCHA ancora valido, nessuna nuova chiamata necessaria.');
      }
    };
    generateToken();
    // eslint-disable-next-line
  }, [executeRecaptcha, token, tokenTimestamp]);

  const handleDownloadPDF = async () => {
    setIsDownloading(true); // disabilita il pulsante
    try {
      if (!executeRecaptcha) {
        console.error('reCAPTCHA non è pronto');
        showAlert('Errore reCAPTCHA. Riprova più tardi.');
        setIsDownloading(false);
        return;
      }

      showAlert(null);

      const now = Date.now();
      let tokenToUse = token;
      if (!token || !tokenTimestamp || now - tokenTimestamp >= TOKEN_VALIDITY_DURATION) {
        console.log('Token reCAPTCHA scaduto, generazione di un nuovo token...');
        tokenToUse = await executeRecaptcha('prenotazione');
        setToken(tokenToUse);
        setTokenTimestamp(now);
      }

      const nome = sessionStorage.getItem('nomeAssociato');
      const cognome = sessionStorage.getItem('cognomeAssociato');
      const codicePrenotazione = sessionStorage.getItem('codicePrenotazione');
      if (!nome || !cognome || !codicePrenotazione) {
        showAlert('Dati per il download non disponibili.');
        setIsDownloading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BASE_SERVER_URI}/site/getpdfprenotazione`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 'nome': nome, 'cognome': cognome, 'id_voucher': voucherID, 'g-recaptcha-response': tokenToUse }),
      });

      if (!response.ok) {
        // Prova a leggere il messaggio di errore dal body
        let errorMsg = 'Errore durante il download del PDF';
        try {
          // Prova a leggere come JSON
          const errorData = await response.clone().json();
          if (errorData && errorData.message) {
            errorMsg = errorData.message;
          }
        } catch (e) {
          // Se non è JSON, prova come testo
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMsg = errorText;
            }
          } catch (e2) {
            // Lascia il messaggio di default
          }
        }
        throw new Error(errorMsg);      
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'prenotazione.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showAlert('Impossibile scaricare il PDF: ' + error.message);
    } finally {
      setIsDownloading(false); // riabilita il pulsante
    }
  };

  const handleCancelAppointment = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error('reCAPTCHA non è pronto');
      showAlert('Errore reCAPTCHA. Riprova più tardi.');
      return;
    }

    setIsCancelling(true); // disabilita il pulsante
    showAlert(null);

    const now = Date.now();
    let tokenToUse = token;
    if (!token || !tokenTimestamp || now - tokenTimestamp >= TOKEN_VALIDITY_DURATION) {
      console.log('Token reCAPTCHA scaduto, generazione di un nuovo token...');
      tokenToUse = await executeRecaptcha('prenotazione');
      setToken(tokenToUse);
      setTokenTimestamp(now);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_SERVER_URI}/visits/selfdelappointment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 'id_associato': associatedID, 'id_voucher': voucherID, 'g-recaptcha-response': tokenToUse }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Errore durante la cancellazione dell\'appuntamento');
      }

      showAlert('Appuntamento cancellato con successo!', 'success');
      setSelectedSlot({ giorno: null, ora: null });
      setCurrentSlot({ giorno: null, ora: null });
      fetchSlots();
    } catch (error) {
      showAlert("Riprova più tardi. "+error.message);
    } finally {
      setIsCancelling(false); // riabilita il pulsante
    }
  }, [token, tokenTimestamp, associatedID, voucherID, showAlert, setSelectedSlot, setCurrentSlot, executeRecaptcha, fetchSlots]);


  const now = new Date();
  const appointmentDate = new Date(currentSlot.giorno);
  const lastDayToCancel = getLastDayBefore(appointmentDate);
  const canCancel = canCancelAppointment(appointmentDate, now);

  return (
    <Card>
      <CardHeader>Appuntamento prenotato</CardHeader>
      <CardBody>
        <p>
          {associatedName} ha un appuntamento per il giorno {new Date(currentSlot.giorno).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })}
          {' '} alle ore {
            currentSlot.ora.length > 5 ? currentSlot.ora.substring(11, 16) : currentSlot.ora.slice(0, 5)
          }. L'appuntamento può essere cancellato <b>entro il giorno {lastDayToCancel.toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' })} alle ore 18:00</b>
        </p>
        <Row className="justify-content-center">
          <Col xs="auto">
            <Button
              color="danger"
              onClick={handleCancelAppointment}
              disabled={isCancelling || !canCancel}
            >
              {isCancelling
                ? 'Cancellazione in corso...'
                : !canCancel
                  ? 'Cancellazione non più disponibile'
                  : 'Cancella appuntamento'}
            </Button>
          </Col>
          <Col xs="auto">
            <Button
              color="info"
              className="mb-3"
              onClick={handleDownloadPDF}
              disabled={isCancelling || isDownloading}
              >
              {isDownloading || isCancelling ? 'Operazione in corso...': 'Scarica PDF prenotazione'}
            </Button>
          </Col>          
          <Col xs="auto">
            <Button
              color="secondary"
              className="mb-3"
              onClick={() => {
                showAlert(null);
                handleValidazione(false, null, null, null, null, null, null);
              }}
              >
              {isCancelling || isDownloading ? 'Operazione in corso...' : 'Prenota per altro socio'}
            </Button>        
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default CancellaPrenotazione;
